import { HasCreatedTimestamp } from '@ts/shared/created-edited/util-core';
import { Money } from '@ts/shared/money/util-core';

export type PaymentMethodCode =
  | 'bca_virtual_account'
  | 'doku_bank_transfer'
  | 'doku_ewallet'
  | 'doku_qris'
  | 'doku_qris_order_offline'
  | 'point_of_sales'
  | 'none';

// Mirrors `PaymentSourceTypeCode` in py/mono/solvent/payment/types.py.
export const PAYMENT_SOURCE_TYPE_CODES = [
  'bca:virtual-account',
  'doku:ewallet',
  'doku:qris',
  'doku:bank-transfer',
  'legacy',
  'offline:cash',
  'offline:credit-card',
  'offline:debit-card',
  'offline:emoney',
  'offline:gift-card',
  'offline:qris',
  'offline:transfer',
  'wallet',
] as const;

export type PaymentSourceTypeCode = (typeof PAYMENT_SOURCE_TYPE_CODES)[number];

export function isPaymentSourceTypeCode(
  value: string,
): value is PaymentSourceTypeCode {
  return (PAYMENT_SOURCE_TYPE_CODES as readonly string[]).includes(value);
}

export type PointOfSalesPaymentSourceTypeCode = PaymentSourceTypeCode &
  (
    | 'offline:cash'
    | 'offline:credit-card'
    | 'offline:debit-card'
    | 'offline:emoney'
    | 'offline:gift-card'
    | 'offline:qris'
  );

export const POINT_OF_SALES_CASH_SOURCE_TYPE_CODE: PointOfSalesPaymentSourceTypeCode =
  'offline:cash';

export const POINT_OF_SALES_GIFT_CARD_SOURCE_TYPE_CODE: PointOfSalesPaymentSourceTypeCode =
  'offline:gift-card';

export const WALLET_PAYMENT_SOURCE_TYPE_CODE: PaymentSourceTypeCode = 'wallet';

export const paymentSourceTypeCodeNameMap: Record<
  PaymentSourceTypeCode,
  string
> = {
  'bca:virtual-account': 'Virtual Account',
  'doku:ewallet': 'E-Wallet',
  'doku:qris': 'DOKU QRIS',
  'doku:bank-transfer': 'DOKU Transfer',
  legacy: 'Legacy',
  'offline:cash': 'Cash',
  'offline:credit-card': 'Credit Card',
  'offline:emoney': 'E-Money',
  'offline:debit-card': 'Debit Card',
  'offline:gift-card': 'Gift Card',
  'offline:qris': 'QRIS',
  'offline:transfer': 'Bank Transfer',
  wallet: 'Wallet',
};

export type TransactionType = 'Debit' | 'Refund';

export interface PaymentMethodBase {
  code: PaymentMethodCode;
}

export type PaymentMethod = Readonly<PaymentMethodBase>;

export interface PaymentMethodWithDetailsBase {
  payment_cost: Money;
  payment_method: PaymentMethod;
}

export type PaymentMethodWithDetails = Readonly<PaymentMethodWithDetailsBase>;

export interface PaymentInfoBase {
  payment_method_with_details: PaymentMethodWithDetails;
}

export type PaymentInfo = Readonly<PaymentInfoBase>;

export interface PaymentInfoWithWalletBase extends PaymentInfoBase {
  wallet_payment_amount: Money;
}

export type PaymentInfoWithWallet = Readonly<PaymentInfoWithWalletBase>;

export interface PointOfSalesPaymentMethodLineBase {
  payment_source_type_code: PointOfSalesPaymentSourceTypeCode;
  amount: Money;
  reference: string;
}

export type PointOfSalesPaymentMethodLine =
  Readonly<PointOfSalesPaymentMethodLineBase>;

export interface OrderOfflinePaymentMethodDataBase {
  // Cashier id:
  cashier: number;

  change_amount: Money;
  lines: readonly PointOfSalesPaymentMethodLine[];
}

export type OrderOfflinePaymentMethodData =
  Readonly<OrderOfflinePaymentMethodDataBase>;

export interface PaymentInfoCheckoutFullBase extends PaymentInfoWithWallet {
  // If order platform is offline, this must be filled:
  order_offline_payment_method_data?: OrderOfflinePaymentMethodData;
}

export type PaymentInfoCheckoutFull = Readonly<PaymentInfoCheckoutFullBase>;

export type PaymentSourceType = Readonly<{
  code: PaymentSourceTypeCode;
}>;

export interface TransactionBase extends HasCreatedTimestamp {
  readonly amount: Money;
  readonly reference: string;
  readonly type: TransactionType;
  readonly payment_source_type: PaymentSourceType;
}

export type Transaction = Readonly<TransactionBase>;
